// GET /postventa-dashboard/observations — org-wide postventa observation
// feed for the dashboard (postventa share-link feature, S1, pulled
// forward from the originally-planned S4 slice: the multi-select share
// UI needs something real to select from). Deliberately NOT the
// facility-dashboard's 17-subquery aggregate shape — a single
// organization_id + observation_status-filtered query against the
// findings(organization_id, observation_status, created_at) index (see
// apps/core-api/src/db/schema.ts), one LEFT JOIN to assets for display.
//
// Gated server-side on `recipe.observationStatuses.length > 0`, never on
// `recipe.slug` — any future vertical that adopts the asset-scoped
// observation lifecycle gets this feed for free.
import { DERIVED_SEVERITIES, INSIGHT_SEVERITIES, INTAKE_CHANNELS, INSIGHT_SIGNALS, OBSERVATION_PHASES, ownerSurveyKindSchema, } from "@bitacorapro/core-engine";
import { z } from "zod";
import { assetObservationSchema } from "./assets.js";
import { observationEvidenceItemSchema, ownerDescriptionSchema, severityOriginSchema, } from "./common.js";
import { criticalAlertMarkSchema } from "./critical-alerts.js";
// `recinto` (ADR-0122) se omite a propósito: es la proyección resuelta
// que el feed MÓVIL necesita porque el catálogo no puede cruzarle el
// wire. El dashboard es la superficie autenticada de escritorio y ya
// depende del paquete de catálogo en build time (ver el comentario de
// scripts/fitness/taxonomy-catalog-never-on-the-wire.mjs sobre por qué
// apps/web-dashboard queda deliberadamente fuera del scan), así que
// heredarlo acá agregaría una segunda representación del mismo hecho sin
// destrabar nada. Mover el decoding código→nombre que le queda al
// dashboard al servidor sigue siendo el follow-up que ese mismo
// comentario nombra — y ahí este omit es lo que hay que revisar.
export const postventaObservationSchema = assetObservationSchema.omit({ recinto: true }).extend({
    assetId: z.string().uuid(),
    /** "<address>, unidad <unit>" — display label so the org-wide feed
     *  doesn't need a second round-trip per row to resolve which
     *  property an observation belongs to. */
    assetLabel: z.string(),
    /** El proyecto al que pertenece la unidad. El journey del panel entra
     *  por Proyectos (handoff 2026-09-15), así que la vista de un proyecto
     *  necesita saber qué filas le corresponden; una unidad suelta, sin
     *  grupo, viaja con `null` y no aparece en ninguna vista de proyecto. */
    assetGroupId: z.string().uuid().nullable(),
    /** Contratista asignado. Lo escribe `POST /findings/:id/assign` desde
     *  ADR-0128 §5; antes de eso `findings.provider_id` no se poblaba en
     *  postventa y la columna «Contratista» de la cola era incalculable. */
    provider: z.object({ id: z.string().uuid(), shortName: z.string() }).nullable(),
    // Gate de confianza (handoff 2026-09-01, D1/D2). `revisionPendiente`
    // and `otrPendiente` are computed by the SAME server predicate that
    // feeds taxonomy-metrics' lowConfidencePending / otrPending, so the
    // KPI tile count and the filtered list always agree. The client never
    // re-derives the threshold from aiConfidence. All optional so the
    // hand-built test fixtures keep compiling.
    revisionPendiente: z.boolean().nullable().optional(),
    otrPendiente: z.boolean().nullable().optional(),
    // ADR-0163 — del vocabulario del engine (suma `consulta_propietario`).
    origen: z.enum(INTAKE_CHANNELS).nullable().optional(),
    specialtyCode: z.string().nullable().optional(),
    confianzaReviewAt: z.string().datetime().nullable().optional(),
    aiConfidence: z.number().min(0).max(1).nullable().optional(),
    // ── S2: read model derivado en el servidor (ADR-0105 / ADR-0129) ──
    // Todo `.optional()` (ADR-0021 D6): los fixtures a mano del dashboard
    // siguen compilando y un core-api anterior al campo sigue parseando.
    /** Dónde está la fila, derivado por `observationPhase` del engine —
     *  el cliente no compara claves de estado ni `observationCycle` contra
     *  literales (fitness: observation-phase-single-source). */
    observationPhase: z.enum(OBSERVATION_PHASES).nullable().optional(),
    /** «No conforme»: de vuelta en triage tras al menos una reapertura.
     *  Es una lectura, NO un estado. El conteo de reaperturas para el
     *  modificador «Reabierta ×N» es `reopenCount`, heredado de
     *  `assetObservationSchema` (vale en cualquier fase). */
    reopened: z.boolean().nullable().optional(),
    /** ADR-0141 — apelación del rechazo en curso (modificador derivado,
     *  mismo patrón que `reopened`). Alimenta el bucket «Apeladas». */
    appealed: z.boolean().nullable().optional(),
    /** La S-code cruda de la clasificación (columna), si la hubo. */
    derivedSeverity: z.enum(DERIVED_SEVERITIES).nullable().optional(),
    /** La S-code que el badge PINTA: la derivada, o el `legacyDisplayAs`
     *  de la receta para una fila anterior a la taxonomía. */
    effectiveSeverity: z.enum(DERIVED_SEVERITIES).nullable().optional(),
    severityOrigin: severityOriginSchema.nullable().optional(),
    /** Códigos crudos de ítem y tipo de falla (ADR-0129) — superficie de
     *  supervisor, como en `GET /findings/:id`. */
    itemCode: z.string().nullable().optional(),
    failureTypeCode: z.string().nullable().optional(),
    /** Las mismas fotos que `evidenceUrls`, con leyenda. */
    evidence: z.array(observationEvidenceItemSchema).optional(),
    /** Lo que quien reportó dijo, con la redacción regulatoria respetada
     *  — ver `ownerDescriptionSchema`. */
    ownerDescriptions: z.array(ownerDescriptionSchema).optional(),
    // ── Listado de observaciones (handoff 3) ──
    /** Número de la unidad (`assets.unit`), sin la dirección: la columna
     *  «Unidad» del listado. */
    assetUnit: z.string().nullable().optional(),
    /** Nombre de la partida (`itemCode`) decodificado contra la versión de
     *  taxonomía con que se estampó la fila. Null sin `itemCode` o si el
     *  código no existe en ese catálogo. */
    partidaNombre: z.string().nullable().optional(),
    /** Ventana del último ciclo asignado (`observation_assigned_at` /
     *  `observation_due_at`). El plazo sólo está vivo en fase asignada; se
     *  conserva tras resuelta/cerrada para medir cumplimiento. Null antes
     *  de asignar y tras reabrir (no conformidad). También null en filas
     *  asignadas antes de que existiera la columna (= «sin plazo»). */
    assignedAt: z.string().datetime().nullable().optional(),
    dueAt: z.string().datetime().nullable().optional(),
    /** Largo del plazo en días corridos, derivado de las dos fechas. */
    plazoDias: z.number().int().positive().nullable().optional(),
    /** ADR-0156 — la marca del aviso inmediato por observación crítica.
     *  Null si nunca se avisó (no era Crítica al nacer) o si quien lee no
     *  es un rol de gestión. */
    criticalAlert: criticalAlertMarkSchema.nullable().optional(),
});
export const postventaObservationsQuery = z
    .object({
    status: z.string().min(1).max(64).optional(),
    /** Acota el feed a un proyecto. Se filtra en el SERVIDOR y no en el
     *  cliente: la vista de un proyecto de 400 unidades no puede pagar
     *  el feed entero de la inmobiliaria para descartar el 90%. */
    assetGroupId: z.string().uuid().optional(),
})
    .strict();
export const postventaObservationsResponse = z.object({
    observations: z.array(postventaObservationSchema),
});
// GET /postventa-dashboard/taxonomy-metrics — instrumentación de la
// taxonomía desde el día 1 (#0698/#0604): volumen de la cola OTR (la
// señal de qué le falta al catálogo — spec §2 vigila explícitamente los
// hongos por condensación), pendientes del gate de confianza, y el
// acumulado de matrix_gap. Counts-only, jamás filas — la cola misma se
// lee con el feed de observaciones filtrado.
export const postventaTaxonomyMetricsResponse = z.object({
    /** Observaciones en OTR sin revisión humana — la cola OTR viva. */
    otrPending: z.number().int().nonnegative(),
    /** Total histórico OTR (revisadas incluidas) — la serie de volumen. */
    otrTotal: z.number().int().nonnegative(),
    /** Clasificadas con confianza bajo el umbral (o NULL) sin revisión —
     *  exactamente las que el gate de emisión retendría hoy. */
    lowConfidencePending: z.number().int().nonnegative(),
    /** Filas de auditoría taxonomy.matrix_gap de la org — insumo de la
     *  revisión trimestral de la matriz (gobernanza de promoción ≥20
     *  queda fuera de alcance, spec §11: esto es solo lectura). */
    matrixGapTotal: z.number().int().nonnegative(),
});
// GET /postventa-dashboard/insights — el panel «Requiere tu atención»
// (docs/exploracion-fase2-seed-panel-chat.md §4). Calculado al leer, sin
// persistencia: el servidor detecta, puntúa y ordena (rankInsights de
// core-engine) y el cliente pinta. Enums derivados de core-engine, no
// espejados. Ninguna tarjeta carga texto del propietario ni del
// contratista: `facts` son plantillas es-CL con números, códigos,
// folios y unidades.
//
// Desde la decisión del founder del 2026-09-30, cada tarjeta es un TIPO
// de señal: la cabecera resume («3 emergencias sin visita») y los casos
// viajan adentro, en `members`. Así el tope de 7 muestra hasta 7 señales
// distintas en vez de 7 casos sueltos de la misma.
export const postventaInsightsQuery = postventaObservationsQuery.pick({ assetGroupId: true });
export const postventaInsightSubjectSchema = z
    .object({
    kind: z.enum(["proyecto", "unidad", "contratista", "partida", "observacion"]),
    id: z.string().min(1),
    label: z.string(),
})
    .strict();
/** Toda acción es un enlace a una URL existente del dashboard; el feed
 *  nunca muta. */
const postventaInsightActionSchema = z
    .object({ label: z.string(), href: z.string().startsWith("/") })
    .strict();
/** Un caso dentro de la tarjeta: una emergencia, el bloque de vencidas
 *  de un proyecto, una partida repetida, un contratista… La granularidad
 *  la decide cada detector. */
export const postventaInsightMemberSchema = z
    .object({
    subject: postventaInsightSubjectSchema,
    /** Proyecto del caso; null cuando no cuelga de uno (contratista). */
    projectId: z.string().uuid().nullable(),
    /** Casos que este miembro afirma (observaciones, unidades, visitas…). */
    count: z.number().int().nonnegative(),
    /** Tamaño de la muestra detrás del caso. */
    n: z.number().int().nonnegative(),
    /** Señal estadística con n < 5: «Pocos casos (n=…): tómalo como una pista». */
    fewCases: z.boolean(),
    facts: z.array(z.string()),
    action: postventaInsightActionSchema,
    score: z.number().finite(),
})
    .strict();
export const postventaInsightSchema = z
    .object({
    signal: z.enum(INSIGHT_SIGNALS),
    /** La más alta de sus casos. */
    severity: z.enum(INSIGHT_SEVERITIES),
    /** El score del caso más alto: ordena las tarjetas. */
    score: z.number().finite(),
    /** Suma de `count` de TODOS los casos (también los que no se listan). */
    count: z.number().int().nonnegative(),
    /** Suma de `n` de todos los casos. */
    n: z.number().int().nonnegative(),
    /** Todos los casos son «pocos casos»: la tarjeta entera es una pista. */
    fewCases: z.boolean(),
    /** Resumen es-CL de la cabecera: «3 emergencias sin visita». */
    title: z.string().min(1),
    why: z.object({ threshold: z.string() }).strict(),
    /** Acción de la tarjeta: la del caso si es uno solo; la lista filtrada
     *  del proyecto si todos caen en el mismo; si no, la del caso más
     *  urgente. */
    action: postventaInsightActionSchema,
    /** Hasta INSIGHT_MEMBER_CAP (10) casos, por score desc. */
    members: z.array(postventaInsightMemberSchema).min(1),
    /** Casos que no se listan: el «y N más». */
    moreCount: z.number().int().nonnegative(),
})
    .strict();
export const postventaInsightsResponse = z
    .object({
    generatedAt: z.string().datetime(),
    items: z.array(postventaInsightSchema),
    scopeSummary: z
        .object({
        /** Proyectos distintos dentro del alcance del llamador. */
        projects: z.number().int().nonnegative(),
        /** Ninguna tarjeta de severidad no positiva: «Todo dentro de lo normal». */
        allClear: z.boolean(),
    })
        .strict(),
})
    .strict();
// ─── ADR-0142 — cierre por falta de respuesta, para el jefe y el gerente ──
//
// GET /postventa-dashboard/closure-summary?assetGroupId= — lo que el
// panel necesita además de `autoApprovalAt` en cada fila:
//   - el conteo APARTE de las cerradas por falta de respuesta, separado
//     de las cerradas con conformidad (un cierre automático no es una
//     conformidad y no se suma a esa métrica);
//   - la lista de observaciones BLOQUEADAS porque al propietario no le
//     llegan los correos: mientras un aviso no sale bien, la observación
//     no se cierra nunca, y el jefe tiene que contactarlo por otra vía.
// Mismo alcance que el feed (`loadScopedAssets`) y el mismo filtro por
// proyecto. Sólo roles de gestión (`isFindingManagementRole`).
export const postventaClosureSummaryQuery = postventaObservationsQuery.pick({
    assetGroupId: true,
});
/** Por qué no le llega el correo al propietario:
 *  - `sin_propietario`: la unidad no tiene propietario vinculado;
 *  - `cuenta_no_activa`: el propietario no tiene una cuenta activa en la
 *    inmobiliaria (nunca activó su invitación, o se desactivó), así que
 *    no hay a qué dirección escribirle;
 *  - `envio_fallido`: el proveedor de correo rechazó o no respondió el
 *    envío. Se reintenta en el próximo tick. */
export const closureBlockReasonSchema = z.enum([
    "sin_propietario",
    "cuenta_no_activa",
    "envio_fallido",
]);
export const postventaClosureBlockedSchema = z
    .object({
    findingId: z.string().uuid(),
    assetId: z.string().uuid(),
    assetGroupId: z.string().uuid().nullable(),
    /** «Av. Mirador 4500, unidad 402» — mismo rótulo que el feed. */
    assetLabel: z.string(),
    assetUnit: z.string().nullable(),
    folio: z.string().nullable(),
    /** Título de la observación (mismo respaldo que los correos). */
    observationLabel: z.string(),
    reason: closureBlockReasonSchema,
    /** Qué aviso no salió: un recordatorio (`reminderNumber` 1-based) o
     *  el aviso de cierre (la observación YA está cerrada; el correo se
     *  sigue reintentando mientras dure la ventana de reapertura). */
    notice: z.enum(["reminder", "closed"]),
    reminderNumber: z.number().int().positive().nullable(),
    attempts: z.number().int().nonnegative(),
    lastAttemptAt: z.string().datetime().nullable(),
})
    .strict();
export const postventaClosureSummaryResponse = z
    .object({
    /** El cierre por falta de respuesta corre para esta organización en
     *  este entorno (receta con el reloj + job activo). false = no hay
     *  fechas de cierre que mostrar y la lista viene vacía. */
    enabled: z.boolean(),
    /** Cerradas con la conformidad del propietario. */
    closedByConformity: z.number().int().nonnegative(),
    /** Cerradas por falta de respuesta (`auto_approval`). */
    closedNoResponse: z.number().int().nonnegative(),
    /** Esperando la confirmación del propietario («Lista para revisar»
     *  sin cierre). */
    awaitingOwner: z.number().int().nonnegative(),
    /** Bloqueadas porque al propietario no le llegan los correos, el
     *  intento más reciente primero. */
    blocked: z.array(postventaClosureBlockedSchema),
})
    .strict();
// ─── ADR-0153 — encuestas del propietario, para el jefe y el gerente ──
//
// GET /postventa-dashboard/survey-summary?assetGroupId= — el NPS de cada
// ola (2 y 9 meses de la entrega), su distribución y su tasa de
// respuesta; la encuesta de la visita técnica, con su promedio general y
// por contratista; y las últimas respuestas (con o sin comentario) con su
// unidad (sin el nombre del propietario), más el total.
//
// Lo ven el gerente_postventa (toda la org), el jefe de proyecto (sus
// proyectos) y el admin interno, con la regla de alcance de siempre (`loadScopedAssets`); un
// proyecto fuera de alcance da 404. El contratista nunca: no tiene cuenta
// del dashboard y nada de esto viaja por `share_link`.
//
// Un número sólo con `OWNER_SURVEY_MIN_N` (5) respuestas o más; con menos,
// null («Pocas respuestas»). El promedio por contratista es una
// evaluación de un tercero (ADR-0153, ROPA): sólo se muestra, nunca
// alimenta una decisión automática (ni ranking ni asignación).
export const postventaSurveySummaryQuery = postventaObservationsQuery.pick({
    assetGroupId: true,
});
const surveyCount = z.number().int().nonnegative();
export const postventaNpsWaveSummarySchema = z
    .object({
    /** Encuestas enviadas: la fila existe y no quedó sin destinatario
     *  (`recipient_not_owner`, que no se envía a nadie). */
    enviadas: surveyCount,
    respondidas: surveyCount,
    promotores: surveyCount,
    pasivos: surveyCount,
    detractores: surveyCount,
    /** −100..100; null con menos de 5 respuestas. */
    nps: z.number().int().min(-100).max(100).nullable(),
    /** Cuántas respuestas por puntaje, de 0 a 10. */
    dist: z.array(surveyCount).length(11),
})
    .strict();
export const postventaVisitSurveySummarySchema = z
    .object({
    enviadas: surveyCount,
    respondidas: surveyCount,
    /** 1..5 con un decimal; null con menos de 5 respuestas. */
    promedio: z.number().min(1).max(5).nullable(),
    /** Cuántas respuestas por puntaje, de 1 a 5. */
    dist: z.array(surveyCount).length(5),
})
    .strict();
export const postventaSurveyProviderSchema = z
    .object({
    providerId: z.string().uuid(),
    /** El nombre corto del contratista al momento de la conformidad. */
    shortName: z.string(),
    respondidas: surveyCount,
    /** null con menos de 5 respuestas: «Pocas respuestas». */
    promedio: z.number().min(1).max(5).nullable(),
})
    .strict();
export const postventaSurveyAnswerSchema = z
    .object({
    surveyId: z.string().uuid(),
    kind: ownerSurveyKindSchema,
    assetId: z.string().uuid(),
    /** «Av. Mirador 4500, unidad 402» — mismo rótulo que el feed. */
    assetLabel: z.string(),
    /** «402» — la tabla del proyecto muestra sólo la unidad (mismo criterio que el cierre). */
    assetUnit: z.string().nullable(),
    score: z.number().int(),
    /** El comentario opcional; null si no escribió. */
    comment: z.string().nullable(),
    respondedAt: z.string().datetime(),
    /** Cuándo escribió el comentario (puede ser días después del número). */
    commentedAt: z.string().datetime().nullable(),
    /** La observación evaluada (sólo la encuesta de visita). */
    findingId: z.string().uuid().nullable(),
})
    .strict();
/** Las respuestas que viajan en el resumen: las más recientes. */
export const POSTVENTA_SURVEY_ANSWERS_MAX = 200;
export const postventaSurveySummaryResponse = z
    .object({
    /** La receta encuesta (`recipe.ownerSurveys`). false = no hay nada
     *  que mostrar y el dashboard oculta la pestaña y el tile. */
    enabled: z.boolean(),
    nps2m: postventaNpsWaveSummarySchema,
    nps9m: postventaNpsWaveSummarySchema,
    visita: postventaVisitSurveySummarySchema,
    /** Por contratista, el de más respuestas primero. */
    porContratista: z.array(postventaSurveyProviderSchema),
    /** Las respuestas, con o sin comentario, la de actividad más reciente
     *  primero (el comentario, o si no el número); a lo más 200. Un
     *  detractor que no escribió también aparece, para hacerle
     *  seguimiento. */
    respuestas: z.array(postventaSurveyAnswerSchema).max(POSTVENTA_SURVEY_ANSWERS_MAX),
    /** Cuántas respuestas hay en total (puede pasar de 200). */
    respuestasTotal: surveyCount,
})
    .strict();
//# sourceMappingURL=postventa-dashboard.js.map